import React, { useEffect, useState } from "react";
import { RouterGuard } from "react-router-guard";
import router from "./router";
import "./app.component.scoped.scss";
// import "@mantine/tiptap/styles.css";
import { GlobalProvider } from "./store";
import createMuiTheme from "@material-ui/core/styles/createMuiTheme";
import { ThemeProvider } from "@material-ui/core";
import { MuiPickersUtilsProvider } from "@material-ui/pickers";
import MomentUtils from "@date-io/moment";
import { DashboardService } from "./services/dashboard/dashboard.service";
import { ServerMessage } from "./components/server-message/server-message.component";
import { LocalStorageService } from "./services/local-storage/local-storage";

const App = () => {
  const dashboardService: DashboardService = new DashboardService();
  const localStorageService: LocalStorageService = new LocalStorageService();
  const lang = localStorageService.getData("lang");

  const empObj: any = {};
  const [configuration, setConfiguration] = useState(empObj);

  const theme = createMuiTheme({
    palette: {
      primary: {
        main: "#a18dba",
        light: "#f2ecfa",
        dark: "#5e057e",
        contrastText: "#FFFFFF",
      },
      secondary: {
        main: "#1d1a21",
        light: "#1d1a216e",
        dark: "#121014",
        contrastText: "#FFFFFF",
      },
      text: {
        primary: "#110813",
        secondary: "#4b5675",
        disabled: "#808080",
        hint: "#99a1b7",
      },
    },
    overrides: {
      MuiInputBase: {
        input: {
          "&::placeholder": {
            color: "rgb(128, 128, 128)",
            opacity: 1,
          },
        },
      },
      MuiButton: {
        containedPrimary: {
          color: "#FFFFFF", // Text color
          backgroundColor: "var(--accent-color) !important", // accent color for the button
          "&:hover": {
            backgroundColor: "var(--dark-accent) !important", // Hover state for accent button
          },
          "&:disabled": {
            backgroundColor: "var(--accent-color) !important", // accent color for the button
            opacity: 0.6,
          },
          // Override the default accent color
        },
        containedSecondary: {
          color: "#FFFFFF", // Text color
          backgroundColor: "var(--secondary-color) !important", // accent color for the button
          "&:hover": {
            backgroundColor: "var(--dark-secondary) !important", // Hover state for accent button
          },
          "&:disabled": {
            backgroundColor: "var(--secondary-color) !important", // accent color for the button
            opacity: 0.6,
          },
        },
        outlinedPrimary: {
          color: "var(--accent-color)",
          border: "1px solid var(--accent-color) !important", // accent color for the button
          "&:hover": {
            borderColor: "var(--dark-accent) !important", // Hover state for accent button
          },
          "&:disabled": {
            color: "var(--accent-color)",
            opacity: 0.6,
          },
        },
        outlinedSecondary: {
          color: "var(--primary-text)",
          border: "1px solid var(--border) !important",
          "&:disabled": {
            color: "var(--primary-text)",
            opacity: 0.6,
          },
        },
        textPrimary: {
          color: "var(--accent-color)",
          "&:disabled": {
            color: "var(--accent-color)",
            opacity: 0.6,
          },
        },
        textSecondary: {
          color: "var(--secondary-text)",
          "&:disabled": {
            color: "var(--secondary-text)",
            opacity: 0.6,
          },
        },
      },
      MuiChip: {
        root: {
          borderRadius: "6px",
          "& .MuiChip-label": {
            color: "#FFFFFF",
          },
        },
        colorPrimary: {
          backgroundColor: "var(--accent-color) !important",
          "& .MuiChip-label": {
            color: "#FFFFFF",
          },
        },
        colorSecondary: {
          backgroundColor: "var(--secondary-color) !important",
          "& .MuiChip-label": {
            color: "#FFFFFF",
          },
        },
        outlinedPrimary: {
          border: "1px solid var(--accent-color) !important",
          backgroundColor: "transparent !important",
          "& .MuiChip-label": {
            color: "var(--accent-color) !important",
          },
        },
        outlinedSecondary: {
          border: "1px solid var(--secondary-color) !important",
          backgroundColor: "transparent !important",
          "& .MuiChip-label": {
            color: "var(--secondary-text) !important",
          },
        },
      },
      MuiLinearProgress: {
        bar: {
          backgroundColor: "var(--accent-color) !important",
        },
        barColorPrimary: {
          backgroundColor: "var(--accent-color) !important",
        },
      },
    },
    typography: {
      fontFamily: "Inter, sans-serif !important",
      h1: {
        fontSize: "1.5rem", // 24px (Text H1)
        fontWeight: 600,
        lineHeight: "28px",
        marginBottom: "0.5rem",
      },
      h2: {
        fontSize: "1.25rem", // 20px (Text H2)
        fontWeight: 500,
        lineHeight: "24px",
        marginBottom: "0.5rem",
      },
      h3: {
        fontSize: "1rem", // 16px (Text H3)
        fontWeight: 500,
        lineHeight: "20px",
      },
      h4: {
        fontSize: "0.875rem", // 14px (Text H4 - Text Default Bold)
        fontWeight: 500,
        lineHeight: "18px",
      },
      h5: {
        fontSize: "0.75rem", // 12px (Text H5)
        fontWeight: 500,
        lineHeight: "18px",
      },
      h6: {
        fontSize: "0.625rem", // 10px (Text H6)
        fontWeight: 500,
        lineHeight: "18px",
      },
      subtitle1: {
        fontSize: "1.5rem", // 24px (Text KPI - H1 Regular)
        fontWeight: 400,
        lineHeight: "28px",
      },
      subtitle2: {
        fontSize: "1rem", // 16px (Text KPI - H3 Regular)
        fontWeight: 400,
        lineHeight: "20px",
      },
      body1: {
        fontSize: "0.875rem", // 14px (Text KPI - H4 Regular)
        fontWeight: 400,
        lineHeight: "20px",
      },
      body2: {
        fontSize: "0.75rem", // 12px (Text KPI - H5 Regular - Text Default Regular)
        fontWeight: 400,
        lineHeight: "18px",
      },
      caption: {
        fontSize: "0.625rem", // 10px
        fontWeight: 300,
        color: "#979797",
      },
    },
  });

  const getConfiguration = async () => {
    const response = await dashboardService.getConfiguration();
    setConfiguration(response);
  };

  const close = () => {
    setConfiguration({ ...configuration, system_availability: 0 });
  };

  useEffect(() => {
    getConfiguration();
    if (!lang) {
      localStorageService.setData("lang", "DE");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <GlobalProvider>
      <MuiPickersUtilsProvider utils={MomentUtils}>
        <ThemeProvider theme={theme}>
          <div className="main-wrapper">
            {configuration && !!+configuration.system_availability && (
              <ServerMessage close={close} />
            )}
            <RouterGuard config={router} />
          </div>
        </ThemeProvider>
      </MuiPickersUtilsProvider>
    </GlobalProvider>
  );
};

export default App;
